import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { Link2, ReceiptText, TrendingUp, FileWarning, ArrowRight } from "lucide-react";

/** Compact TDS tile for the Command Centre — real-time snapshot of the org's TDS health. */
export default function TDSDashboardTile() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/tds/dashboard-summary").then((r) => setS(r.data)).catch(() => setS({}));
  }, []);
  if (!s) return null;

  const totalOpen = (s.open_tds_payable || 0) + (s.open_tds_receivable || 0);
  if (totalOpen === 0 && (s.total_challans || 0) === 0 && (s.mismatches_26as || 0) === 0) return null;

  const items = [
    { label: "Unmapped challans", value: s.unmapped_challans || 0, amount: s.unmapped_amount,
      to: "/app/tds", tone: (s.unmapped_challans || 0) > 0 ? "warning" : "muted", icon: Link2,
      testid: "tds-tile-unmapped" },
    { label: "Open TDS Payable", value: fmtINR(s.open_tds_payable || 0), sub: "Deducted, not deposited",
      to: "/app/tds", tone: (s.open_tds_payable || 0) > 0 ? "danger" : "success", icon: ReceiptText,
      testid: "tds-tile-payable" },
    { label: "Open TDS Receivable", value: fmtINR(s.open_tds_receivable || 0), sub: "Claim in ITR",
      to: "/app/tds", tone: "info", icon: TrendingUp,
      testid: "tds-tile-receivable" },
    { label: "26AS mismatches", value: s.mismatches_26as || 0, sub: "Only in 26AS",
      to: "/app/tds", tone: (s.mismatches_26as || 0) > 0 ? "danger" : "success", icon: FileWarning,
      testid: "tds-tile-26as" },
  ];
  const toneMap = { muted: "text-slate-700 bg-slate-50", info: "text-blue-700 bg-blue-50",
                     success: "text-emerald-700 bg-emerald-50", warning: "text-amber-700 bg-amber-50",
                     danger: "text-red-700 bg-red-50" };

  return (
    <div className="card-sharp p-5 space-y-3" data-testid="tds-dashboard-tile">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">TDS · Compliance snapshot</div>
          <div className="text-xs text-muted-foreground mt-0.5">Unmapped challans · open payable · claimable receivable · 26AS mismatches — one click drills to the TDS console.</div>
        </div>
        <Link to="/app/tds" className="text-xs font-mono uppercase text-accent flex items-center gap-1 hover:underline" data-testid="tds-tile-openall">
          Open console <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <Link key={i} to={it.to} className={`block border rounded-sm p-3 hover:shadow transition ${toneMap[it.tone]}`}
                    data-testid={it.testid}>
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest opacity-70">
                <Icon className="w-3 h-3" /> {it.label}
              </div>
              <div className="text-2xl font-medium mt-1 tabular-nums">{it.value}</div>
              {it.amount !== undefined && it.amount > 0 && (
                <div className="text-[10px] font-mono opacity-70">{fmtINR(it.amount)}</div>
              )}
              {it.sub && <div className="text-[10px] font-mono opacity-70">{it.sub}</div>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
